// Übersicht aller Übergabeprotokolle (Etappe 1, Task 10) — letzter Baustein des
// Moduls: ohne diese Seite ist der fertige Assistent (Task 8/9) über keine Route
// erreichbar. Route-Muster wie TicketListe: /admin/uebergabeprotokolle (Liste) und
// /admin/uebergabeprotokolle/:id (rendert bei gesetzter Kennung den Assistenten an
// derselben Stelle statt der Liste — siehe TicketListe.tsx routeTicketId).
//
// Optik: Widget-Stil nach design_handoff_uebergabe (17.10.2026) im globalen Hell/Dunkel —
// Kopf, Art-Widgets, Filterzeile und Karten liegen in ./liste/ListenTeile.tsx.
//
// Seit Etappe 1.5/Task 1 entsteht eine Kopfzeile in `uebergabeprotokolle` (Status
// "in_erfassung") bereits beim Bestätigen der Vorauswahl-Pille (starteUebergabe unten),
// nicht erst beim Abschluss — das Büro soll eine laufende Übergabe sofort sehen. Diese
// Seite lädt deshalb ZWEI Quellen: die DB-Tabelle (`ladeProtokolle`, alle Status) und
// `listeOffene()` (lokale, auf DIESEM Gerät noch nicht hochgeladene Entwürfe). Beide
// können zur selben Kennung existieren (Normalfall bei Netz); Fix-Runde 1 (Review)
// führt sie deshalb über `lib/uebergabe/listenzeilen.ts` zu GENAU EINER Zeile pro
// Kennung zusammen, statt sie wie zuvor unabhängig doppelt anzuzeigen (Fund 1).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase/client';
import { erzeugeAusVorlage } from '../../../lib/uebergabe/vorlage';
import { gegenstueckArt } from '../../../lib/uebergabe/kopieren';
import { usePillenSteuerung } from '../../../components/island/widgets/SteuerungsAnsicht';
import { listeOffene, loescheEntwurf, speichereEntwurf, erzeugeEntwurf, type LokalerEntwurf } from '../../../lib/uebergabe/lokal';
import { schliesseAb, echteDeps, abschlussMeldungen } from '../../../lib/uebergabe/abschluss';
import { pruefeAbschlussBereit, PFLICHT_ROLLEN } from '../../../lib/uebergabe/unterschriften';
import { kartenAnsicht, zaehleArten, type ArtOderAlle, type KartenAktion } from '../../../lib/uebergabeAnsicht/darstellung';
import { ArtWidgets, FilterZeile, ListenKopf, UebergabeKarte } from './liste/ListenTeile';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { Sheet } from '../../../components/ui/Sheet';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { ArrowsLeftRight, CheckCircle, CircleNotch, ClipboardText, Funnel, XCircle } from '@phosphor-icons/react';
import { legeKopfzeileAn, ladeKontakteFuerBezug, verteileRollen, type Bezug } from '../../../lib/uebergabe/bezug';
import { fuehreZeilenZusammen, istEndgueltig, type DatensatzZeile, type ZusammengefuehrteZeile, type Zeilenzustand } from '../../../lib/uebergabe/listenzeilen';
import { holeServerStatus } from '../../../lib/uebergabe/serverstatus';
import { pruefeStornoErlaubt } from '../../../lib/uebergabe/storno';
import type { Art, Partei, Protokoll, ProtokollStatus } from '../../../lib/uebergabe/typen';
import UebergabeAssistent from './UebergabeAssistent';
import UebergabeVorauswahl from './UebergabeVorauswahl';
import LadeHinweis from './LadeHinweis';
import UebergabeHausmeister from '../../../components/uebergabe/UebergabeHausmeister';
import { ladeFreigabenZu, type UebergabeFreigabe } from '../../../lib/uebergabe/hausmeisterFreigabe';

interface UebergabeListeProps {
  userProfile?: any;
}

// ProtokollZeile ist strukturell identisch zu DatensatzZeile (lib/uebergabe/listenzeilen.ts)
// — bewusst so gehalten, damit `protokolle` unten ohne Umbau als erstes Argument von
// fuehreZeilenZusammen dient.
type ProtokollZeile = DatensatzZeile;

// Zu welchem Filter-Bucket ("Alle"/"In Erfassung"/"Abgeschlossen") ein Zustand gehört —
// wartet_auf_upload/lokal_in_bearbeitung/in_erfassung_fremd zählen für den Filter alle
// als "in_erfassung" (kein eigener Filter-Chip für "abgebrochen": das sieht man nur
// unter "Alle", damit es nicht in den Hauptfiltern auftaucht).
const ZUSTAND_FILTER_STATUS: Record<Zeilenzustand, ProtokollStatus> = {
  lokal_in_bearbeitung: 'in_erfassung',
  wartet_auf_upload: 'in_erfassung',
  in_erfassung_fremd: 'in_erfassung',
  abgeschlossen: 'abgeschlossen',
  abgebrochen: 'abgebrochen',
};
// Nur für den Hinweistext bei veraltetem lokalem Entwurf ("... zu diesem inzwischen
// {ADJEKTIV} Protokoll") — entwurfVeraltet ist nur bei diesen beiden Endzuständen wahr.
const ZUSTAND_ADJEKTIV: Record<'abgeschlossen' | 'abgebrochen', string> = {
  abgeschlossen: 'abgeschlossenen',
  abgebrochen: 'abgebrochenen',
};

// Optionen der Pillen-Steuerung (unverändert; sichtbar auf der Seite heißt „Kauf“ „Übergabe an Eigentümer“).
const ART_FILTER_OPTIONEN: { wert: Art | 'alle'; label: string }[] = [
  { wert: 'alle', label: 'Alle' },
  { wert: 'einzug', label: 'Einzug' },
  { wert: 'auszug', label: 'Auszug' },
  { wert: 'kauf', label: 'Kauf' },
];
const STATUS_FILTER_OPTIONEN: { wert: ProtokollStatus | 'alle'; label: string }[] = [
  { wert: 'alle', label: 'Alle' },
  { wert: 'in_erfassung', label: 'In Erfassung' },
  { wert: 'abgeschlossen', label: 'Abgeschlossen' },
];

// "Von → an": Übergeber/Übernehmer sind die eigentlichen Vertragsparteien; der Zeuge
// (optional, meist der erfassende Mitarbeiter) würde die Zeile nur unnötig verlängern.
const parteienZeile = (parteien: Partei[] | null | undefined): string => {
  const von = parteien?.find(p => p.rolle === 'uebergeber')?.name?.trim();
  const an = parteien?.find(p => p.rolle === 'uebernehmer')?.name?.trim();
  if (!von && !an) return 'Keine Parteien erfasst';
  return `${von || '—'} → ${an || '—'}`;
};

// Fund Task 4/Schritt 3 (Abschluss-Review): hat wirklich NIEMAND eine Mailadresse
// hinterlegt, darf die Liste nicht dasselbe "Nicht versendet" wie bei einem echten
// Sendefehler zeigen — ein erneuter Versuch würde am selben Fehlen scheitern (das
// Protokoll ist nach dem Abschluss unveränderlich). Rein clientseitig aus den ohnehin
// geladenen Parteien abgeleitet, keine eigene Server-Spalte nötig.
//
// Fund W2 (Review): Gezählt werden nur die VERTRAGSPARTEIEN — der Zeuge ist der
// eingeloggte Mitarbeiter und hat praktisch immer eine Adresse, die Zeile hätte sonst
// dauerhaft ein "Mail erneut senden" angeboten, das nie zu einem gültigen Versand führen
// kann (die Function wertet den Versand seit W2 ebenfalls nur an Übergeber/Übernehmer,
// s. supabase/functions/uebergabe-finalisieren/empfaenger.ts).
const hatEmailAdresse = (parteien: Partei[] | null | undefined): boolean =>
  !!parteien?.some(p => PFLICHT_ROLLEN.includes(p.rolle) && p.email?.trim());

const formatDatum = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatZeitpunkt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Fix-Runde 2 ("Wichtig, gleich mitnehmen"): die Abbrechen-Rückfrage sagte bisher nur
// "der lokale Entwurf wird entfernt" — ohne zu nennen, WAS das ist. Bis zum Abschluss
// liegen Fotos, Zählerstände, Schlüssel und bereits geleistete Unterschriften
// AUSSCHLIESSLICH lokal (die Kopfzeile in der DB kennt nur den Bezug), ein Abbruch
// entfernt das unwiderruflich. Es gibt keinen gespeicherten "zuletzt erreichten
// Schritt" (schritt ist reiner UI-State im Assistenten, nicht Teil des Protokolls) —
// deshalb wird er hier aus den vorhandenen Daten geschätzt (höchster Schritt mit
// erkennbaren Spuren), nicht exakt nachgehalten.
//
// Fix-Runde 3 (kleiner Review-Fund): raeume/pruefpunkte/fotos/zaehler/schluessel/
// unterschriften können bei einem unvollständigen Entwurf fehlende oder kaputte
// Einträge enthalten (kein Array, einzelne null-Werte) — ungeschützt iteriert würde das
// VOR der Rückfrage werfen und den Abbrechen-Knopf lahmlegen (der Klick täte dann
// scheinbar gar nichts). Dieselbe Absicherung wie bereits an derselben Stelle in
// supabase/functions/uebergabe-finalisieren/index.ts (dortiger Fund 4).
function beschreibeUmfang(p: Protokoll): string {
  const raeume = Array.isArray(p.raeume) ? p.raeume : [];
  const zaehler = Array.isArray(p.zaehler) ? p.zaehler : [];
  const schluessel = Array.isArray(p.schluessel) ? p.schluessel : [];
  const unterschriften = Array.isArray(p.unterschriften) ? p.unterschriften.length : 0;

  const fotos = raeume.reduce((summe, r) => {
    if (!r) return summe;
    const pruefpunkte = Array.isArray(r.pruefpunkte) ? r.pruefpunkte : [];
    const pruefpunktFotos = pruefpunkte.reduce((s, pp) => s + (pp && Array.isArray(pp.fotos) ? pp.fotos.length : 0), 0);
    // Praxis-Ergänzung: Raum-Fotos (Zustandsdokumentation, unabhängig von Mängeln) zählen
    // hier mit — sonst sähe „bislang erfasst" nach einem Rundgang voller Raumfotos ohne
    // einen einzigen Mangel fälschlich nach 0 Fotos aus.
    const raumFotos = Array.isArray(r.fotos) ? r.fotos.length : 0;
    return summe + pruefpunktFotos + raumFotos;
  }, 0) + zaehler.filter(z => !!z?.foto).length;

  let schrittLabel = 'Rahmen';
  if (unterschriften > 0) schrittLabel = 'Unterschriften';
  else if (p.bemerkungen?.trim()) schrittLabel = 'Bemerkungen';
  else if (schluessel.some(s => (s?.anzahl ?? 0) > 0)) schrittLabel = 'Schlüssel';
  else if (zaehler.length > 0) schrittLabel = 'Zähler-Kontrolle';
  else if (raeume.length > 0) schrittLabel = 'Rundgang';

  const raeumeText = raeume.length === 1 ? '1 Raum' : `${raeume.length} Räume`;
  const fotosText = fotos === 1 ? '1 Foto' : `${fotos} Fotos`;
  const unterschriftenText = unterschriften === 1 ? '1 Unterschrift' : `${unterschriften} Unterschriften`;
  return `Zuletzt bearbeitet: ${schrittLabel} — bislang erfasst: ${raeumeText}, ${fotosText}, ${unterschriftenText}.`;
}

const UebergabeListe: React.FC<UebergabeListeProps> = ({ userProfile }) => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();

  const [protokolle, setProtokolle] = useState<ProtokollZeile[]>([]);
  const [loading, setLoading] = useState(true);
  const [ladeFehler, setLadeFehler] = useState(false);
  const [offeneEntwuerfe, setOffeneEntwuerfe] = useState<LokalerEntwurf[]>([]);
  // Best-effort-Objektname für Zeilen ohne Datensatz (zustand "wartet_auf_upload" — der
  // lokale Entwurf kennt nur die objekt_id, keinen Namen, s. UebergabeZeileKarte).
  const [objekteMap, setObjekteMap] = useState<Record<string, string>>({});
  // Best-effort-Name zu erfasst_von (nur profiles.id → full_name) für die "Seit .../von"-
  // Zeile bei laufenden Erfassungen (Fund 2, Review).
  const [profilMap, setProfilMap] = useState<Record<string, string>>({});
  // Bezug aus der Vorauswahl-Pille für die zuletzt selbst angelegte Übergabe — gilt nur
  // für GENAU diese Kennung (s. neueUebergabe/starteUebergabe). Der Assistent selbst
  // entscheidet trotzdem: ein bereits vorhandener lokaler Entwurf gewinnt immer gegen
  // diesen Bezug (UebergabeAssistent.tsx), hier wird er nur bei echter Neuanlage genutzt.
  const [vorausgewaehlterBezug, setVorausgewaehlterBezug] = useState<{ id: string; bezug: Bezug } | null>(null);

  const [suche, setSuche] = useState('');
  const [artFilter, setArtFilter] = useState<Art | 'alle'>('alle');
  const [statusFilter, setStatusFilter] = useState<ProtokollStatus | 'alle'>('alle');

  const [sendetId, setSendetId] = useState<string | null>(null);
  const [erzeugtId, setErzeugtId] = useState<string | null>(null);
  const [ladeLinkId, setLadeLinkId] = useState<string | null>(null);
  const [abbrechtId, setAbbrechtId] = useState<string | null>(null);
  const [vorlageId, setVorlageId] = useState<string | null>(null);
  const [dmsId, setDmsId] = useState<string | null>(null);

  const { mobil } = useMobilModus();
  // Rückfragen und Meldungen im App-Stil (statt window.confirm/alert, Freigabe 17.10.2026)
  const { melde, bestaetige, dialoge } = useAppMeldungen(mobil);

  const ladeProtokolle = useCallback(async () => {
    setLadeFehler(false);
    const { data, error } = await supabase
      .from('uebergabeprotokolle')
      .select('id, art, status, durchgefuehrt_am, parteien, pdf_path, drive_file_id, mail_versendet_am, erfasst_von, objekt:objekte(id, bezeichnung), einheit:objekt_einheiten(id, bezeichnung)')
      .order('durchgefuehrt_am', { ascending: false });
    if (error) { setLadeFehler(true); setLoading(false); return; }
    setProtokolle((data ?? []) as unknown as ProtokollZeile[]);
    setLoading(false);
  }, []);
  useEffect(() => { void ladeProtokolle(); }, [ladeProtokolle]);

  const ladeOffene = useCallback(async () => {
    try {
      setOffeneEntwuerfe(await listeOffene());
    } catch {
      // Lokaler Speicher (IndexedDB) nicht erreichbar — die Zeile zeigt dann einfach
      // keinen lokalen Entwurf, die DB-Liste funktioniert unabhängig davon weiter.
      setOffeneEntwuerfe([]);
    }
  }, []);
  useEffect(() => { void ladeOffene(); }, [ladeOffene]);

  // Best effort, nur zur Beschriftung von Zeilen ohne Datensatz (lokale Entwürfe kennen
  // nur die objekt_id, keinen Namen). Schlägt der Abruf fehl, zeigt die Zeile eben nur
  // Art + Zeit ohne Objektnamen.
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from('objekte').select('id, bezeichnung');
      if (error || !data) return;
      const map: Record<string, string> = {};
      (data as { id: string; bezeichnung: string }[]).forEach(o => { map[o.id] = o.bezeichnung; });
      setObjekteMap(map);
    })();
  }, []);

  // Best effort, nur zur Beschriftung "Seit ... · <Name>" bei laufenden Erfassungen.
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from('profiles').select('id, full_name');
      if (error || !data) return;
      const map: Record<string, string> = {};
      (data as { id: string; full_name: string | null }[]).forEach(p => { if (p.full_name) map[p.id] = p.full_name; });
      setProfilMap(map);
    })();
  }, []);

  // Serverseitiger Schutz gegen einen veralteten lokalen Entwurf (Task 3, Einfrieren): Die
  // DB lehnt seit der Migration 2026-07-26-uebergabe-einfrieren.sql jede inhaltliche
  // Änderung UND jeden Statuswechsel an einer bereits 'abgeschlossen'en ODER
  // 'abgebrochen'en Zeile hart ab (Fix-Runde 2: der Trigger — und dieser Client-Guard —
  // kannten bisher nur 'abgeschlossen'; ein zwischenzeitlich abgebrochenes Protokoll
  // ließ sich unbemerkt trotzdem fertig abschließen). Genau das kann auch hier
  // passieren, wenn `jetztSenden` unten den Server-Abschluss erfolgreich abschließt, das
  // anschließende `loescheEntwurf` aber scheitert (z. B. Verbindungsabbruch direkt
  // danach) — der lokale Entwurf bleibt dann mit Stand 'in_erfassung' liegen, obwohl der
  // Server-Datensatz längst fertig ist. Ein Reload/Direktlink auf genau diese Route oder
  // ein erneuter Klick auf "Jetzt senden" würde ohne diese Prüfung die harte Postgres-
  // Exception der Einfrier-Migration als rohe Fehlermeldung zeigen. Schlägt die Prüfung
  // selbst fehl (kein Netz), wird — wie überall sonst in diesem Modul — nicht blockiert,
  // sondern durchgelassen (fail open).
  //
  // holeServerStatus/istEndgueltig sind gemeinsam mit UebergabeAssistent.tsx genutzt
  // (lib/uebergabe/serverstatus.ts, lib/uebergabe/listenzeilen.ts), damit beide Stellen
  // denselben Endgültigkeits-Begriff verwenden und nicht wieder auseinanderlaufen können.
  //
  // Räumt einen veralteten lokalen Entwurf (samt Notanker, s. loescheEntwurf) auf und zeigt
  // einen verständlichen Hinweis statt einer Fehlermeldung — genutzt sowohl automatisch
  // (Route öffnen, "Jetzt senden") als auch manuell ("Lokalen Entwurf verwerfen" auf einer
  // bereits abgeschlossenen/abgebrochenen Zeile, s. UebergabeZeileKarte).
  const verwerfeVeraltetenEntwurf = useCallback(async (id: string, serverStatus: ProtokollStatus | null) => {
    try {
      await loescheEntwurf(id);
    } catch {
      // IndexedDB/localStorage evtl. nicht erreichbar — der Hinweis erscheint trotzdem,
      // der Server-Datensatz ist ohnehin bereits endgültig.
    }
    await ladeOffene();
    melde(serverStatus === 'abgebrochen'
      ? 'Diese Übergabe wurde inzwischen abgebrochen.'
      : 'Dieses Protokoll wurde bereits abgeschlossen.');
    // melde ist stabil (useCallback ohne Deps-Wechsel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ladeOffene]);

  // Kennung, für die die Server-Prüfung oben bereits grünes Licht gegeben hat — erst dann
  // darf der Assistent unten tatsächlich rendern (statt eines blanko-neuen Entwurfs unter
  // einer schon vergebenen Kennung oder eines zum Scheitern verurteilten Weiterarbeitens).
  const [routeFrei, setRouteFrei] = useState<string | null>(null);

  useEffect(() => {
    if (!routeId) return;
    let abgebrochen = false;
    (async () => {
      const serverStatus = await holeServerStatus(routeId);
      if (abgebrochen) return;
      if (istEndgueltig(serverStatus)) {
        await verwerfeVeraltetenEntwurf(routeId, serverStatus);
        if (abgebrochen) return;
        navigate('/admin/uebergabeprotokolle', { replace: true });
        return;
      }
      setRouteFrei(routeId);
    })();
    return () => { abgebrochen = true; };
    // verwerfeVeraltetenEntwurf/navigate bewusst nicht in den Deps (Muster wie der
    // Lade-Effekt in UebergabeAssistent.tsx) — nur ein Wechsel der Kennung selbst soll
    // die Prüfung neu anstoßen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Bestätigung der Vorauswahl-Pille: Kennung erzeugen, Kontakte auflösen und Parteien
  // verteilen, den lokalen Entwurf DAMIT SOFORT anlegen (Fix-Runde 1, Review: vorher
  // entstand er erst im Assistenten — ein sehr schneller Reload direkt nach "Übergabe
  // starten" hätte den Bezug verloren, weil der nur im Arbeitsspeicher dieser Liste
  // lebt). Kopfzeile (Status "in Erfassung", damit das Büro die laufende Übergabe
  // sofort sieht) danach, Formular schließen, zum Assistenten navigieren.
  //
  // Schlägt das Laden der Kontakte fehl (kein Netz), wird OHNE vorbelegte Parteinamen
  // angelegt (Objekt/Einheit/IDs aus dem Bezug bleiben erhalten). Schlägt legeKopfzeileAn
  // fehl, darf das die Erfassung NICHT blockieren — sie läuft lokal weiter und wird beim
  // Abschluss serverseitig nachgeholt (schliesseAb upsertet dieselbe Zeile erneut).
  const starteUebergabe = async (bezug: Bezug) => {
    const id = crypto.randomUUID();

    let parteien: Partei[] | undefined;
    try {
      const kontakte = await ladeKontakteFuerBezug(bezug);
      parteien = verteileRollen(bezug, kontakte, {
        name: userProfile?.full_name ?? '',
        email: userProfile?.email ?? null,
      });
    } catch {
      parteien = undefined;
    }
    const entwurf = erzeugeEntwurf(id, userProfile, bezug, parteien);
    try {
      await speichereEntwurf(entwurf);
    } catch (err) {
      // IndexedDB blockiert o. Ä. — der Assistent versucht es beim Laden über bezug
      // (weiterhin per vorausgewaehlterBezug unten) defensiv erneut.
      console.warn('Lokaler Entwurf konnte beim Anlegen nicht gesichert werden.', err);
    }

    try {
      await legeKopfzeileAn(bezug, id, userProfile?.id ?? null);
    } catch (err: any) {
      melde('Kopfzeile konnte nicht angelegt werden (keine Verbindung?): ' + (err?.message || 'Unbekannter Fehler')
        + '\nDie Erfassung startet trotzdem — der Datensatz entsteht beim Abschluss.', true);
    }
    setVorauswahlOffen(false);
    setVorausgewaehlterBezug({ id, bezug });
    void ladeOffene();
    navigate(`/admin/uebergabeprotokolle/${id}`);
  };

  // Neue Übergabe als Sheet über der Seite (Handoff §3; vorher Pillen-Formular, gleiche Breite 560).
  const [vorauswahlOffen, setVorauswahlOffen] = useState(false);
  const neueUebergabe = () => setVorauswahlOffen(true);

  // Identischer Ablauf wie UebergabeAssistent.abschliessen(): Erfolg hängt
  // ausschließlich an schliesseAb; scheitert nur das anschließende lokale Aufräumen,
  // ist das Protokoll serverseitig trotzdem fertig und darf NIE als Fehlschlag gelten.
  //
  // Die Unterschriften-Sperre liegt seit dem Abschluss-Review in schliesseAb selbst
  // (lib/uebergabe/abschluss.ts, Fund K1) — ein bei Schritt 2 abgebrochener Entwurf wird
  // hier also mit klarer Meldung abgewiesen, statt ohne Unterschriften abgeschlossen,
  // verschickt und danach gelöscht zu werden.
  const jetztSenden = async (entwurf: LokalerEntwurf) => {
    if (sendetId) return;
    setSendetId(entwurf.id);
    try {
      // Derselbe Server-Schutz wie beim Öffnen der Route (s. oben) — ohne ihn würde ein
      // erneuter Klick auf einen bereits abgeschlossenen ODER abgebrochenen, nur lokal
      // noch nicht gelöschten Entwurf die harte Postgres-Exception der Einfrier-Migration
      // als rohen Fehler zeigen.
      const serverStatus = await holeServerStatus(entwurf.id);
      if (istEndgueltig(serverStatus)) {
        await verwerfeVeraltetenEntwurf(entwurf.id, serverStatus);
        return;
      }
      const erg = await schliesseAb(entwurf, echteDeps());
      try {
        await loescheEntwurf(entwurf.id);
      } catch {
        melde('Protokoll ist abgeschlossen; der lokale Entwurf konnte nicht entfernt werden.', true);
      }
      await Promise.all([ladeOffene(), ladeProtokolle()]);
      const meldungen = abschlussMeldungen(erg);
      if (meldungen) melde(`Protokoll abgeschlossen.\n\n${meldungen}`);
    } catch (err: any) {
      melde('Senden fehlgeschlagen: ' + (err?.message || 'Unbekannter Fehler')
        + '\nDas Protokoll bleibt auf diesem Gerät gespeichert und kann später erneut gesendet werden.', true);
    } finally {
      setSendetId(null);
    }
  };

  // Ruft dieselbe Function wie der reguläre Abschluss — sie ist laut Design idempotent
  // (überschreibt PDF/Mails nur, wenn mail_versendet_am noch leer ist). Bedient sowohl
  // „Erneut erzeugen" (PDF fehlt) als auch „Mail erneut senden" (Fund W1: Versand ist
  // komplett gescheitert, der Vermerk wurde zurückgerollt, ein neuer Versuch greift).
  const erneutFinalisieren = async (id: string) => {
    if (erzeugtId) return;
    setErzeugtId(id);
    try {
      const erg = await echteDeps().finalisiere(id);
      await ladeProtokolle();
      const teile: string[] = [];
      if (erg.mails?.length) teile.push(`E-Mail versendet an: ${erg.mails.join(', ')}`);
      const meldungen = abschlussMeldungen(erg);
      if (meldungen) teile.push(meldungen);
      melde(teile.length ? teile.join('\n\n') : 'PDF wurde neu erzeugt; die E-Mail war bereits zuvor versendet.');
    } catch (err: any) {
      melde('Erneuter Versuch fehlgeschlagen: ' + (err?.message || 'Unbekannter Fehler'), true);
    } finally {
      setErzeugtId(null);
    }
  };

  const pdfHerunterladen = async (p: ZusammengefuehrteZeile) => {
    if (!p.pdf_path || ladeLinkId) return;
    setLadeLinkId(p.id);
    try {
      const { data, error } = await supabase.storage.from('uebergaben').createSignedUrl(p.pdf_path, 3600);
      if (error || !data?.signedUrl) {
        melde('PDF-Link konnte nicht erzeugt werden: ' + (error?.message || 'Unbekannter Fehler'), true);
        return;
      }
      window.open(data.signedUrl, '_blank', 'noopener');
    } finally {
      setLadeLinkId(null);
    }
  };

  // Dieselbe Wohnung, naechste Uebergabe: aus dem abgeschlossenen Protokoll einen neuen
  // lokalen Entwurf bauen (Raeume, Zustaende, Fotos, Zaehler, Schluessel) und direkt in
  // den Assistenten springen — dort aendert man nur noch, was anders ist.
  const alsVorlageVerwenden = async (zeile: ZusammengefuehrteZeile) => {
    if (vorlageId) return;
    setVorlageId(zeile.id);
    try {
      const ziel = gegenstueckArt(zeile.art);
      const { entwurfId, fehlendeBilder } = await erzeugeAusVorlage(zeile.id, ziel, userProfile);
      if (fehlendeBilder > 0) {
        melde(`${fehlendeBilder} Foto(s) der Vorlage konnten nicht geladen werden und fehlen im neuen Entwurf.`, true);
      }
      navigate(`/admin/uebergabeprotokolle/${entwurfId}`);
    } catch (e) {
      melde('Kopieren fehlgeschlagen: ' + ((e as Error)?.message || 'Unbekannter Fehler'), true);
    } finally {
      setVorlageId(null);
    }
  };

  const insDmsAblegen = async (zeile: ZusammengefuehrteZeile) => {
    if (dmsId) return;
    setDmsId(zeile.id);
    try {
      const { error } = await supabase.functions.invoke('gdrive', {
        body: { action: 'uebergabe-ablegen', protokollId: zeile.id },
      });
      // supabase-js meldet bei Function-Fehlern nur "non-2xx"; der Grund steht im Body.
      if (error) {
        const antwort = (error as { context?: Response }).context;
        let grund = error.message;
        try { grund = (await antwort?.json())?.error || grund; } catch { /* Body nicht lesbar */ }
        throw new Error(grund);
      }
      await ladeProtokolle();
    } catch (e) {
      melde('Ablage im DMS fehlgeschlagen: ' + ((e as Error)?.message || 'Unbekannter Fehler'), true);
    } finally {
      setDmsId(null);
    }
  };

  // Fix-Runde 1 (Review): "In Erfassung"-Zeilen konnten bisher ewig stehen bleiben —
  // kein Status wechselte je zu 'abgebrochen', kein Weg, das zu ändern. Verliert jemand
  // das Tablet oder bricht mittendrin ab, war das von einer normal laufenden Erfassung
  // nicht zu unterscheiden. Der Wechsel nach 'abgebrochen' ist vom Einfrieren-Trigger
  // ausdrücklich als Storno erlaubt (s. Migration 2026-07-26-uebergabe-einfrieren.sql,
  // "Storno bleibt ausdrücklich erlaubt"); ein eventuell auf DIESEM Gerät noch
  // vorhandener lokaler Entwurf wird zusätzlich entfernt, damit er nicht als "wartet auf
  // Upload" wieder auftaucht.
  //
  // Fix-Runde 2 ("Wichtig, gleich mitnehmen"): die Rückfrage nannte bisher nur "der
  // lokale Entwurf wird entfernt", ohne zu sagen, WAS das eigentlich ist — Fotos,
  // Zählerstände, Schlüssel und bereits geleistete Unterschriften liegen bis zum
  // Abschluss AUSSCHLIESSLICH lokal. beschreibeUmfang() macht den Umfang konkret,
  // bevor jemand einen zu 90 % fertigen Termin unwiderruflich verwirft.
  //
  // Fix-Runde 4 (kritischer Review-Fund K2): Bis hierher schrieb diese Funktion
  // status='abgebrochen' OHNE die Serverprüfung, die `jetztSenden` und der Routen-Guard
  // längst benutzen (holeServerStatus). Der Trigger lässt abgeschlossen→abgebrochen als
  // Storno ausdrücklich zu, und diese Liste lädt sich nicht von selbst nach: ein offener
  // Büro-Tab zeigt eine vor Ort inzwischen abgeschlossene Übergabe beliebig lange als
  // „In Erfassung — anderes Gerät". Ein Klick auf „Abbrechen" hätte das fertige,
  // unterschriebene und verschickte Protokoll unwiderruflich storniert (zurück blockiert
  // der Trigger, das PDF verschwindet aus der Oberfläche). Jetzt: erst Serverstand holen
  // (lib/uebergabe/storno.ts), dann fragen, und das Update selbst läuft bedingt über
  // `.eq('status','in_erfassung')` — trifft es keine Zeile, hat jemand im Rennen dazwischen
  // abgeschlossen/abgebrochen, und es passiert nichts.
  const abbrechenErfassung = async (zeile: ZusammengefuehrteZeile) => {
    if (abbrechtId) return;
    setAbbrechtId(zeile.id);
    try {
      // Vor der Rückfrage prüfen: eine Rückfrage zu etwas, das ohnehin abgelehnt wird,
      // wäre nur irreführend.
      const entscheidung = pruefeStornoErlaubt(await holeServerStatus(zeile.id));
      if (!entscheidung.erlaubt) {
        await Promise.all([ladeProtokolle(), ladeOffene()]);
        melde(entscheidung.meldung, true);
        return;
      }

      const umfangText = zeile.entwurf ? beschreibeUmfang(zeile.entwurf.protokoll) : null;
      const nachricht = [
        'Diese Übergabe wirklich abbrechen?',
        umfangText,
        'Der Vorgang wird als „Abgebrochen" markiert'
          + (zeile.entwurf ? ' und der lokale Entwurf auf diesem Gerät entfernt' : '') + '.',
        'Das kann NICHT rückgängig gemacht werden.',
      ].filter(Boolean).join('\n\n');
      if (!(await bestaetige({ nachricht, label: 'Abbrechen', icon: XCircle }))) return;

      const { data: storniert, error } = await supabase
        .from('uebergabeprotokolle')
        .update({ status: 'abgebrochen' })
        .eq('id', zeile.id)
        .eq('status', 'in_erfassung')
        .select('id');
      if (error) {
        // Nachladen wie in den beiden anderen Ausstiegen dieser Funktion: die Zeile stammt
        // aus einem Stand, der zum Zeitpunkt des Fehlers schon überholt sein kann (diese
        // Liste lädt sich nicht von selbst nach). Ohne das Nachladen bliebe sie in einem
        // veralteten Zustand stehen — inklusive der Knöpfe, die dazu gehören.
        await Promise.all([ladeProtokolle(), ladeOffene()]);
        melde('Abbrechen fehlgeschlagen: ' + error.message, true);
        return;
      }
      if (!storniert?.length) {
        // Zwischen Prüfung und Schreiben abgeschlossen/abgebrochen — der lokale Entwurf
        // bleibt hier bewusst liegen; die neu geladene Zeile bietet dafür „Lokalen
        // Entwurf verwerfen" an (entwurfVeraltet).
        const nachStand = pruefeStornoErlaubt(await holeServerStatus(zeile.id));
        await Promise.all([ladeProtokolle(), ladeOffene()]);
        melde(nachStand.erlaubt
          ? 'Diese Übergabe konnte nicht abgebrochen werden — bitte erneut versuchen.'
          : nachStand.meldung, true);
        return;
      }
      if (zeile.entwurf) {
        try {
          await loescheEntwurf(zeile.id);
        } catch {
          // Lokaler Speicher evtl. nicht erreichbar — der Server-Status ist trotzdem gesetzt.
        }
      }
      await Promise.all([ladeProtokolle(), ladeOffene()]);
    } finally {
      setAbbrechtId(null);
    }
  };

  // Fix-Runde 2 (kritischer Review-Fund, Punkt 3): manuelles Aufräumen für
  // zeile.entwurfVeraltet — ein lokaler Entwurf, dessen Datensatz bereits abgeschlossen
  // oder abgebrochen ist (z. B. weil loescheEntwurf nach einem Serverabschluss auf
  // einem anderen Gerät scheiterte). Der Server-Datensatz ist unangetastet, es wird
  // ausschließlich der lokale Entwurf entfernt.
  const entwurfVerwerfen = async (zeile: ZusammengefuehrteZeile) => {
    if (abbrechtId || !zeile.entwurf) return;
    setAbbrechtId(zeile.id);
    try {
      await verwerfeVeraltetenEntwurf(zeile.id, zeile.zustand === 'abgebrochen' ? 'abgebrochen' : 'abgeschlossen');
    } finally {
      setAbbrechtId(null);
    }
  };

  // Wunsch 3 (Praxis-Fund): ein rein lokaler Entwurf ohne jeden Datensatz
  // ("wartet_auf_upload" — z. B. ein versehentlich gestarteter oder geplatzter Termin,
  // oder schlicht kein Netz beim Anlegen) ließ sich bisher NICHT entfernen: "Abbrechen"
  // verlangt eine DB-Kennung (die es hier nie gab), "Lokalen Entwurf verwerfen" einen
  // bereits endgültigen Datensatz (den es hier ebenfalls nicht gibt — s. entwurfVeraltet).
  // Die Zeile blieb dauerhaft als Warnzeile stehen. Serverseitig gibt es hier nichts zu
  // prüfen oder zu markieren (kein Datensatz, kein Storno nötig) — anders als beim
  // bestehenden Storno-Weg (abbrechenErfassung, mit holeServerStatus/pruefeStornoErlaubt)
  // wird deshalb NICHT dieselbe Serverprüfung durchlaufen, sondern ausschließlich lokal
  // gelöscht. ermittleLoeschAktion() (lib/uebergabe/zeilenanzeige.ts) stellt sicher, dass
  // dieser Pfad nur für genau diesen Fall (kein Datensatz, nicht abgeschlossen/
  // abgebrochen) angeboten wird — mit Datensatz bleibt es beim bestehenden,
  // serverseitig geprüften "Abbrechen".
  //
  // Dieselbe abbrechtId-Sperre wie abbrechenErfassung/entwurfVerwerfen: Während
  // IRGENDEINE dieser unwiderruflichen Aktionen läuft, sind alle Zeilen gesperrt.
  const entwurfLokalLoeschen = async (zeile: ZusammengefuehrteZeile) => {
    if (abbrechtId || sendetId || !zeile.entwurf) return;
    setAbbrechtId(zeile.id);
    try {
      const umfangText = beschreibeUmfang(zeile.entwurf.protokoll);
      const nachricht = [
        'Diesen Entwurf wirklich löschen?',
        umfangText,
        'Der Entwurf wird von diesem Gerät entfernt — es gibt dazu keinen Datensatz auf dem Server.',
        'Das kann NICHT rückgängig gemacht werden.',
      ].filter(Boolean).join('\n\n');
      if (!(await bestaetige({ nachricht, label: 'Löschen' }))) return;

      try {
        await loescheEntwurf(zeile.id);
      } catch (err: any) {
        melde('Entwurf konnte nicht gelöscht werden: ' + (err?.message || 'Unbekannter Fehler'), true);
        return;
      }
      await ladeOffene();
    } finally {
      setAbbrechtId(null);
    }
  };

  // Fix-Runde 1 (Review): eine Zeile je Kennung statt zwei unabhängiger Quellen (Fund 1)
  // — s. lib/uebergabe/listenzeilen.ts (reine Funktion, dort auch getestet).
  const zeilen = useMemo(() => fuehreZeilenZusammen(protokolle, offeneEntwuerfe), [protokolle, offeneEntwuerfe]);

  // Hausmeister-Freigaben (Ticket #436) für alle Protokolle in Erfassung — eine Abfrage für die ganze Liste
  const [hausmeisterFreigaben, setHausmeisterFreigaben] = useState<Record<string, UebergabeFreigabe[]>>({});
  const inErfassungIds = useMemo(() => protokolle.filter(p => p.status === 'in_erfassung').map(p => p.id).join(','), [protokolle]);
  const ladeHausmeisterFreigaben = useCallback(async () => {
    setHausmeisterFreigaben(await ladeFreigabenZu(inErfassungIds ? inErfassungIds.split(',') : []));
  }, [inErfassungIds]);
  useEffect(() => { void ladeHausmeisterFreigaben(); }, [ladeHausmeisterFreigaben]);

  // Status + Suche zuerst (Grundlage der Art-Zähler), danach die Art — Art-Widgets sind Filter UND Zähler.
  const imBereich = zeilen.filter(z => {
    // wartet_auf_upload/lokal_in_bearbeitung/in_erfassung_fremd zählen für den Filter
    // alle als "in_erfassung" — s. ZUSTAND_FILTER_STATUS oben.
    if (statusFilter !== 'alle' && ZUSTAND_FILTER_STATUS[z.zustand] !== statusFilter) return false;
    if (suche.trim()) {
      const q = suche.trim().toLowerCase();
      const parteienText = (z.parteien || []).map(x => x.name || '').join(' ');
      const heuhaufen = `${z.objekt?.bezeichnung || ''} ${z.einheit?.bezeichnung || ''} ${parteienText}`.toLowerCase();
      if (!heuhaufen.includes(q)) return false;
    }
    return true;
  });
  const artZaehler = zaehleArten(imBereich);
  const gefiltert = imBereich.filter(z => artFilter === 'alle' || z.art === artFilter);

  const zeigeAssistent = !!routeId;

  // Pillen-Steuerung (Suche + Art-/Status-Filter). WICHTIG: jeder Wert, den die
  // Steuerung liest, muss in den Deps stehen — sonst friert die Pille ihre Handler mit
  // veralteten Closures ein (Vorfall 2026-07-25 im Ticket-Detail, siehe
  // TicketDetailSeite.tsx, usePillenSteuerung). Registrierung muss VOR jedem
  // Early-Return stehen (Rules of Hooks) — Muster wie TicketListe.tsx.
  usePillenSteuerung({
    titel: 'Übergabeprotokolle',
    suche: { platzhalter: 'Suchen…', wert: suche, onChange: setSuche },
    eintraege: [
      { id: 'art', typ: 'auswahl', farbe: 'violett', icon: ArrowsLeftRight, kicker: 'Art', wert: artFilter, standard: 'alle',
        onChange: (w: string) => setArtFilter(w as Art | 'alle'), optionen: ART_FILTER_OPTIONEN.map(o => ({ wert: o.wert, label: o.label })) },
      { id: 'status', typ: 'auswahl', farbe: 'indigo', icon: CheckCircle, kicker: 'Status', wert: statusFilter, standard: 'alle',
        onChange: (w: string) => setStatusFilter(w as ProtokollStatus | 'alle'), optionen: STATUS_FILTER_OPTIONEN.map(o => ({ wert: o.wert, label: o.label })) },
      { id: 'anzahl', typ: 'kennzahl', farbe: 'blau', icon: ClipboardText, kicker: 'Übersicht', werte: [{ label: 'Protokolle gesamt', zahl: zeilen.length }] },
    ],
  }, [suche, artFilter, statusFilter, zeilen.length], !zeigeAssistent);

  // Vollflächen-Assistent statt Liste, wenn eine Kennung in der Route steht — neue
  // Übergabe (frisch erzeugte crypto.randomUUID()) oder "Weiter bearbeiten" eines
  // lokalen Entwurfs landen beide hier, der Assistent selbst unterscheidet nicht.
  if (routeId) {
    // Solange die Server-Prüfung oben für DIESE Kennung noch nicht durchgelaufen ist
    // (routeFrei), zeigt sich nur ein kurzer Ladezustand statt des Assistenten — genau wie
    // dessen eigener Spinner, während er den lokalen Entwurf lädt (UebergabeAssistent.tsx).
    if (routeFrei !== routeId) {
      return (
        <div className="flex items-center justify-center" style={{ minHeight: 300, color: 'var(--acc)' }}>
          <CircleNotch size={32} weight="bold" className="animate-spin" />
        </div>
      );
    }
    // Der Bezug gilt nur für die Kennung, die starteUebergabe selbst erzeugt hat — ein
    // Direktaufruf der Route (Reload, "Weiter bearbeiten" eines lokalen Entwurfs) bekommt
    // hier bewusst null, der Assistent lädt dann ausschließlich den lokalen Entwurf.
    const bezugFuerDiesenEntwurf = vorausgewaehlterBezug?.id === routeId ? vorausgewaehlterBezug.bezug : null;
    return (
      <UebergabeAssistent
        protokollId={routeId}
        userProfile={userProfile}
        bezug={bezugFuerDiesenEntwurf}
        onFertig={() => {
          // Fund Task 4/Schritt 4 (Abschluss-Review): push hätte die Assistenten-Route
          // (jetzt ohne lokalen Entwurf, da erfolgreich abgeschlossen) als Browser-
          // History-Eintrag stehen lassen — ein Tipp auf "Zurück" landet dann wieder auf
          // /admin/uebergabeprotokolle/:id, wo mangels Entwurf ein neuer, leerer angelegt
          // würde. replace ersetzt diesen Eintrag, "Zurück" führt daran vorbei.
          navigate('/admin/uebergabeprotokolle', { replace: true });
          void ladeProtokolle();
          void ladeOffene();
        }}
        meldeDanach={melde}
        onAbbrechen={() => {
          // Dieselbe Begründung wie bei onFertig: onAbbrechen läuft auch, nachdem ein
          // bereits anderswo abgeschlossenes/abgebrochenes Protokoll den lokalen Entwurf
          // hier gelöscht hat (s. UebergabeAssistent.tsx::abschliessen) — derselbe
          // "Zurück führt zu leerem Entwurf"-Fall.
          navigate('/admin/uebergabeprotokolle', { replace: true });
          void ladeOffene();
        }}
      />
    );
  }

  const aktionAusfuehren = (z: ZusammengefuehrteZeile, a: KartenAktion) => {
    switch (a) {
      case 'weiter': case 'hier_weiter': navigate(`/admin/uebergabeprotokolle/${z.id}`); break;
      case 'senden': if (z.entwurf) void jetztSenden(z.entwurf); break;
      case 'erneut_erzeugen': case 'mail_erneut': void erneutFinalisieren(z.id); break;
      case 'pdf': void pdfHerunterladen(z); break;
      case 'ins_dms': void insDmsAblegen(z); break;
      case 'als_vorlage': void alsVorlageVerwenden(z); break;
      case 'verwerfen': void entwurfVerwerfen(z); break;
      case 'abbrechen': void abbrechenErfassung(z); break;
      case 'entwurf_loeschen': void entwurfLokalLoeschen(z); break;
    }
  };

  // Sperrlogik wie bisher: läuft irgendein Versand ODER Abbruch, sind diese Knöpfe in ALLEN Zeilen gesperrt;
  // dasselbe für „Erneut erzeugen“/„Mail erneut senden“ (erzeugtId). Spinner nur in der betroffenen Zeile.
  const aktionZustand = (z: ZusammengefuehrteZeile, bereit: boolean, grund: string | null) => (a: KartenAktion) => {
    const sperre = !!sendetId || !!abbrechtId;
    switch (a) {
      case 'weiter': case 'hier_weiter': case 'verwerfen': return { gesperrt: sperre, laeuft: false };
      case 'senden': return { gesperrt: sperre || !bereit, laeuft: sendetId === z.id, tip: bereit ? undefined : grund ?? undefined };
      case 'abbrechen': case 'entwurf_loeschen': return { gesperrt: sperre, laeuft: abbrechtId === z.id };
      case 'erneut_erzeugen': case 'mail_erneut': return { gesperrt: !!erzeugtId, laeuft: erzeugtId === z.id };
      case 'pdf': return { gesperrt: ladeLinkId === z.id, laeuft: ladeLinkId === z.id };
      case 'ins_dms': return { gesperrt: dmsId === z.id, laeuft: dmsId === z.id };
      case 'als_vorlage': return { gesperrt: vorlageId === z.id, laeuft: vorlageId === z.id };
    }
  };

  return (
    <>
      <GlasTooltipSchicht />
      <ListenKopf anzahl={zeilen.length} mobil={mobil} onNeu={neueUebergabe} />
      <ArtWidgets zaehler={artZaehler} aktiv={artFilter} onWahl={(a: ArtOderAlle) => setArtFilter(a)} />
      <FilterZeile suche={suche} onSuche={setSuche} status={statusFilter} onStatus={setStatusFilter} mobil={mobil} />

      {/* Fix-Runde 1 (Review): jede zusammengeführte Zeile trägt ihren Zustand selbst (eine Karte je Kennung). */}
      {loading ? (
        <div className="flex items-center justify-center" style={{ padding: '80px 0', color: 'var(--acc)' }}>
          <CircleNotch size={32} weight="bold" className="animate-spin" />
        </div>
      ) : ladeFehler ? (
        <div className="immo-glas" style={{ borderRadius: 26, padding: 20 }}>
          <LadeHinweis text="Übergabeprotokolle konnten nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={ladeProtokolle} />
        </div>
      ) : zeilen.length === 0 ? (
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: '70px 24px', gap: 10 }}>
          <span className="inline-flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: 18, background: 'var(--acc-soft)' }}>
            <ClipboardText size={28} weight="fill" color="var(--acc)" />
          </span>
          <span style={{ fontSize: 15, fontWeight: 800 }}>Noch keine Übergaben</span>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)', maxWidth: 360 }}>
            Lege die erste Übergabe an, um Ein-/Auszüge und Eigentümerwechsel vor Ort zu erfassen.
          </span>
        </div>
      ) : gefiltert.length === 0 ? (
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: '70px 24px', gap: 10 }}>
          <Funnel size={30} color="var(--ink3)" />
          <span style={{ fontSize: 13, color: 'var(--ink2)' }}>Keine Protokolle im Filter</span>
        </div>
      ) : (
        <div className="grid items-start" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(min(420px, 100%), 1fr))' }}>
          {gefiltert.map(z => {
            const bereitschaft = z.entwurf ? pruefeAbschlussBereit(z.entwurf.protokoll) : null;
            const ansicht = kartenAnsicht({
              zustand: z.zustand, hatDatensatz: z.hatDatensatz, hatEntwurf: !!z.entwurf, entwurfVeraltet: z.entwurfVeraltet,
              pdf_path: z.pdf_path ?? null, mail_versendet_am: z.mail_versendet_am ?? null, drive_file_id: z.drive_file_id ?? null,
              hatEmailAdresse: hatEmailAdresse(z.parteien), bereitschaft,
            });
            const objektName = z.objekt?.bezeichnung || (z.entwurf?.protokoll.objekt_id ? objekteMap[z.entwurf.protokoll.objekt_id] : undefined);
            return (
              <UebergabeKarte
                key={z.id}
                art={z.art}
                titel={`${objektName || 'Kein Objekt'}${z.einheit?.bezeichnung ? ` · ${z.einheit.bezeichnung}` : ''}`}
                datum={formatDatum(z.durchgefuehrt_am)}
                parteien={parteienZeile(z.parteien)}
                ansicht={ansicht}
                seit={ansicht.inErfassung ? formatZeitpunkt(z.durchgefuehrt_am) : null}
                erfasstVon={z.erfasst_von ? profilMap[z.erfasst_von] ?? null : null}
                veraltetHinweis={z.entwurfVeraltet
                  ? `Auf diesem Gerät liegt noch ein lokaler Entwurf zu diesem inzwischen ${ZUSTAND_ADJEKTIV[z.zustand as 'abgeschlossen' | 'abgebrochen']} Protokoll.`
                  : null}
                versendetAm={z.mail_versendet_am ? formatZeitpunkt(z.mail_versendet_am) : null}
                hausmeister={ansicht.inErfassung ? (
                  // Ticket #436: In Erfassung befindliche Übergaben lassen sich einem Hausmeister freigeben.
                  <UebergabeHausmeister protokollId={z.id} einheitId={z.einheit?.id ?? null} freigaben={hausmeisterFreigaben[z.id] ?? []}
                    userId={userProfile?.id ?? null} onGeaendert={() => void ladeHausmeisterFreigaben()} />
                ) : null}
                zustand={aktionZustand(z, !!bereitschaft?.bereit, bereitschaft?.grund ?? null)}
                onAktion={(a) => aktionAusfuehren(z, a)}
                mobil={mobil}
              />
            );
          })}
        </div>
      )}

      {vorauswahlOffen && (
        <Sheet titel="Neue Übergabe" icon={ClipboardText} iconFarbe="#0a84ff" breite={560} onSchliessen={() => setVorauswahlOffen(false)}>
          <UebergabeVorauswahl onClose={() => setVorauswahlOffen(false)} onStart={(bezug) => void starteUebergabe(bezug)} />
        </Sheet>
      )}
      {dialoge}
    </>
  );
};

export default UebergabeListe;
